/* Mera Naksha — Tentative Construction Budget & EMI cards.
 *
 * Two exported components:
 *   <BudgetEstimateCard area={1200} floors={2} />        — pre-unlock teaser
 *   <BudgetDetailedReport area={1200} floors={2} />      — post-unlock breakdown
 *
 * `area` is the per-floor footprint (sqft); `floors` is the number of
 * constructed floors (defaults to 1). The total built-up area passed
 * to the API is always `area * floors`. When floors > 1, the card
 * shows a floor-wise breakdown (Ground Floor / First Floor / …) and a
 * "Total Built-up Area" line.
 *
 * Both fetch /api/budget/estimate or /api/budget/detailed respectively and
 * silently hide themselves when the master flag is OFF or the area is 0.
 * No additional auth — the public endpoint already enforces flag gating.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { IndianRupee, Wallet, Info, Loader2, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const fmtSqft = (n) => `${Number(n || 0).toLocaleString("en-IN")} sq.ft.`;

// Ordinal floor labels — covers most Tier-3 builds (G to G+3).
const FLOOR_LABELS = [
  "Ground Floor",
  "First Floor",
  "Second Floor",
  "Third Floor",
  "Fourth Floor",
];
const floorLabel = (idx) => FLOOR_LABELS[idx] || `Floor ${idx + 1}`;

/**
 * Renders the per-floor + total-built-up breakdown, or a single-line
 * built-up label for single-floor designs.
 */
const BuiltUpBreakdown = ({ area, floors, testId }) => {
  const safeFloors = Math.max(1, Number(floors) || 1);
  const total = (Number(area) || 0) * safeFloors;
  if (safeFloors <= 1) {
    return (
      <div
        className="text-[12.5px] text-navy font-bold tabular-nums"
        data-testid={testId}
      >
        Built-up Area:{" "}
        <span className="text-navy">{fmtSqft(total)}</span>
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl bg-sand-alt/70 border border-border/60 px-3 py-2 text-[12.5px]"
      data-testid={testId}
    >
      <div className="text-[10.5px] uppercase tracking-wider text-gold-dark font-extrabold inline-flex items-center gap-1.5">
        <Building2 className="h-3 w-3" /> Floor-wise built-up
      </div>
      <ul className="mt-1 space-y-0.5">
        {Array.from({ length: safeFloors }).map((_, i) => (
          <li
            key={`floor-${i}`}
            className="flex items-center justify-between gap-3 tabular-nums"
            data-testid={`${testId}-floor-${i}`}
          >
            <span className="text-muted-foreground">{floorLabel(i)}</span>
            <span className="text-navy font-bold">{fmtSqft(area)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-1.5 pt-1.5 border-t border-border/60 flex items-center justify-between gap-3 tabular-nums">
        <span className="text-navy font-extrabold uppercase text-[11px] tracking-wider">
          Total Built-up Area
        </span>
        <span
          className="text-navy font-extrabold"
          data-testid={`${testId}-total`}
        >
          {fmtSqft(total)}
        </span>
      </div>
    </div>
  );
};

export const BudgetEstimateCard = ({ area, floors = 1 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const safeFloors = Math.max(1, Number(floors) || 1);
  const totalArea = (Number(area) || 0) * safeFloors;

  useEffect(() => {
    let cancel = false;
    if (!totalArea || totalArea <= 0) {
      setLoading(false);
      return undefined;
    }
    axios
      .get(`${API}/budget/estimate`, { params: { area_sqft: totalArea } })
      .then(({ data: d }) => {
        if (!cancel) setData(d);
      })
      .catch(() => {
        if (!cancel) setData(null);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [totalArea]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white/70 border border-border px-4 py-3 text-xs text-muted-foreground inline-flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Estimating…
      </div>
    );
  }
  if (!data || !data.enabled) return null;
  if (!data.budget && !data.emi) return null;

  return (
    <section
      data-testid="budget-estimate-card"
      className="rounded-3xl bg-white border-2 border-navy/10 px-4 py-4 shadow-sm space-y-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold inline-flex items-center gap-1.5">
          <IndianRupee className="h-3.5 w-3.5" />
          Tentative Construction Estimate
        </div>
        <div className="text-[11px] text-muted-foreground tabular-nums">
          <strong className="text-navy">{fmtSqft(totalArea)}</strong> total
        </div>
      </div>

      <BuiltUpBreakdown
        area={area}
        floors={safeFloors}
        testId="budget-estimate-breakdown"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.budget && (
          <div
            className="rounded-2xl bg-sand-alt px-3.5 py-3 border border-border/60"
            data-testid="budget-estimate-budget"
          >
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">
              Construction Budget
            </div>
            <div className="mt-1 text-lg sm:text-xl font-extrabold text-navy tabular-nums leading-tight">
              {fmtINR(data.budget.low)} – {fmtINR(data.budget.high)}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              at ₹{data.budget.rate_min}–₹{data.budget.rate_max} / sqft
            </div>
          </div>
        )}
        {data.emi && (
          <div
            className="rounded-2xl bg-navy text-white px-3.5 py-3 border border-navy-600"
            data-testid="budget-estimate-emi"
          >
            <div className="text-[10.5px] uppercase tracking-wider text-gold font-bold inline-flex items-center gap-1.5">
              <Wallet className="h-3 w-3" />
              Estimated Home-loan EMI
            </div>
            <div className="mt-1 text-lg sm:text-xl font-extrabold text-gold tabular-nums leading-tight">
              {fmtINR(data.emi.low)} – {fmtINR(data.emi.high)}
              <span className="text-xs text-white/60 font-normal">/mo</span>
            </div>
            <div className="mt-0.5 text-[11px] text-white/70">
              @ {data.emi.interest_rate}% p.a. · {data.emi.tenure_years}y · {data.emi.loan_pct}% LTV
            </div>
          </div>
        )}
      </div>

      <div className="text-[11px] text-muted-foreground inline-flex items-start gap-1.5 leading-snug">
        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span>
          {data.emi
            ? "All construction costs and EMI figures are tentative estimates based on prevailing rates in Tier 3/4 markets. Actuals will vary by location, material choice, and bank terms."
            : "Construction cost is a tentative estimate based on prevailing rates in Tier 3/4 markets. Actuals will vary by location and material choice."}
        </span>
      </div>
    </section>
  );
};


export const BudgetDetailedReport = ({ area, floors = 1 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const safeFloors = Math.max(1, Number(floors) || 1);
  const totalArea = (Number(area) || 0) * safeFloors;

  useEffect(() => {
    let cancel = false;
    if (!totalArea || totalArea <= 0) {
      setLoading(false);
      return undefined;
    }
    axios
      .get(`${API}/budget/detailed`, { params: { area_sqft: totalArea } })
      .then(({ data: d }) => {
        if (!cancel) setData(d);
      })
      .catch(() => {
        if (!cancel) setData(null);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [totalArea]);

  if (loading || !data || !data.enabled) return null;
  if (!data.budget && !data.emi) return null;

  const b = data.budget;
  const e = data.emi;

  return (
    <section
      data-testid="budget-detailed-report"
      className="rounded-3xl bg-white border-2 border-border p-5 sm:p-6 shadow-sm"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">
        Premium Unlock · Tentative Construction Report
      </div>
      <h3 className="mt-1 text-xl sm:text-2xl font-extrabold text-navy">
        {e ? "Cost & EMI breakdown" : "Construction cost breakdown"} for {fmtSqft(totalArea)}
      </h3>

      <div className="mt-3">
        <BuiltUpBreakdown
          area={area}
          floors={safeFloors}
          testId="budget-detailed-breakdown"
        />
      </div>

      {/* Budget table */}
      {b && (
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
            Construction Budget
          </div>
          <div className="mt-1 text-2xl font-extrabold text-navy tabular-nums">
            {fmtINR(b.budget_low)} – {fmtINR(b.budget_high)}{" "}
            <span className="text-xs text-muted-foreground font-normal">
              (mid {fmtINR(b.budget_mid)})
            </span>
          </div>
          <div className="text-[11.5px] text-muted-foreground">
            at ₹{b.rate_per_sqft_min}–₹{b.rate_per_sqft_max} / sqft (default ₹{b.rate_per_sqft_default})
          </div>
          <div className="mt-3 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm" data-testid="budget-stage-table">
              <thead className="bg-sand-alt text-navy text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Stage</th>
                  <th className="text-right px-3 py-2 font-bold">% share</th>
                  <th className="text-right px-3 py-2 font-bold">Min</th>
                  <th className="text-right px-3 py-2 font-bold">Mid</th>
                  <th className="text-right px-3 py-2 font-bold">Max</th>
                </tr>
              </thead>
              <tbody>
                {b.stages.map((s) => (
                  <tr
                    key={s.key}
                    className="border-t border-border/60"
                    data-testid={`budget-stage-row-${s.key}`}
                  >
                    <td className="px-3 py-2 font-bold text-navy">{s.label}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {s.pct}%
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtINR(s.amount_low)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-navy">
                      {fmtINR(s.amount_mid)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtINR(s.amount_high)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EMI table */}
      {e && (
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
            Home-loan EMI Estimate
          </div>
          <div className="mt-1 text-2xl font-extrabold text-navy tabular-nums">
            {fmtINR(e.low.monthly_emi)} – {fmtINR(e.high.monthly_emi)}/mo
          </div>
          <div className="text-[11.5px] text-muted-foreground">
            at {e.low.annual_rate_pct}% p.a. for {e.low.tenure_years} years · loan covers {e.low.loan_pct}% of estimated budget
          </div>
          <div className="mt-3 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm" data-testid="budget-emi-table">
              <thead className="bg-sand-alt text-navy text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Item</th>
                  <th className="text-right px-3 py-2 font-bold">Min</th>
                  <th className="text-right px-3 py-2 font-bold">Mid</th>
                  <th className="text-right px-3 py-2 font-bold">Max</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Estimated Budget", "principal_budget"],
                  ["Loan Amount", "principal"],
                  ["Down Payment", "down_payment"],
                  ["Monthly EMI", "monthly_emi"],
                  ["Total Interest", "total_interest"],
                  ["Total Payable", "total_payable"],
                ].map(([label, field]) => (
                  <tr
                    key={field}
                    className="border-t border-border/60"
                    data-testid={`budget-emi-row-${field}`}
                  >
                    <td className="px-3 py-2 font-bold text-navy">{label}</td>
                    {field === "principal_budget" ? (
                      <>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtINR(b.budget_low)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-bold">{fmtINR(b.budget_mid)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtINR(b.budget_high)}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtINR(e.low[field])}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-bold">{fmtINR(e.mid[field])}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtINR(e.high[field])}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 text-[11px] text-muted-foreground inline-flex items-start gap-1.5 leading-snug">
        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span>
          All construction costs and EMI figures are tentative estimates. Please
          consult a licensed builder and your bank for binding quotes.
        </span>
      </div>
    </section>
  );
};

export default BudgetEstimateCard;
